import { cn, swivuRecentRecordSortFn } from "@/modern/utils";
import { SWIVU_TASK_DEFAULT_COLOR } from "@/other/configs/globalConfig";
import { EDITOR_KEYS } from "@/other/constants/EditorKeys";
import { ENTITY_TYPES, VIEW_TYPES } from "@/other/constants/OtherKeys";
import { SWIVU_STANDARD_ICONS } from "@/other/constants/IconKeys.jsx";
import type { Task } from "@/bryntum-lib/models/Task";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { entityFinderRowClass } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { EntityFinderTaskIcon } from "@/modern/components/entity-finder/components/EntityFinderTaskIcon";
import {
  canCreate,
  estimateDefaultRowHeight,
  openEntityEditor,
} from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";
import {
  ENTITY_FINDER_SUBTITLE_CLS,
  ENTITY_FINDER_TITLE_CLS,
} from "@/modern/components/entity-finder/constants/layout";

export const taskConfig: EntityFinderConfigEntry<"task"> = {
  type: "task",
  label: "Task",
  plural: "Tasks",
  icon: <i className={SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks]} aria-hidden />,
  tint: "bg-chart-1/15 text-chart-1",
  listOrder: 10,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "task"),
  getData: (ctx) => {
    const searchLower = ctx.debouncedQuery.toLowerCase();
    const records = swivuRecentRecordSortFn(
      ctx.project.extraTaskStore.query(
        (task: Task) =>
          passesArchiveFilter(task.archived, ctx.isShowArchive) &&
          !task.isLinked &&
          (matchesSearch(task.name, searchLower) || matchesSearch(task.description, searchLower)),
        true
      ) as unknown as Task[],
      { currentBoardUserId: ctx.currentBoardUserId }
    );

    return records.map((task) => ({
      id: `task-${task.id}`,
      type: "task" as const,
      label: task.name || "N/A",
      subtitle: task.description || undefined,
      record: task,
    }));
  },
  getRecentItem: (ctx) => {
    const records = swivuRecentRecordSortFn(
      ctx.project.extraTaskStore.query(
        (task: Task) => passesArchiveFilter(task.archived, ctx.isShowArchive) && !task.isLinked,
        true
      ) as unknown as Task[],
      { currentBoardUserId: ctx.currentBoardUserId }
    ).slice(0, 1);

    const task = records[0];
    if (!task) return null;

    return {
      id: `task-${task.id}`,
      type: "task",
      label: task.name || "N/A",
      subtitle: task.description || undefined,
      record: task,
    };
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(taskConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  itemRenderer: ({ item, query }) => {
    const task = item.record as Task;
    const color = task.color || SWIVU_TASK_DEFAULT_COLOR;
    const hasSubtitle = Boolean(item.subtitle);

    return (
      <div className={entityFinderRowClass(hasSubtitle)}>
        <EntityFinderTaskIcon color={color} hasSubtitle={hasSubtitle} />
        <div className="min-w-0 flex-1">
          <div className={cn("truncate", ENTITY_FINDER_TITLE_CLS)}>
            <EntityFinderHighlight text={item.label} query={query} />
          </div>
          {item.subtitle ? (
            <p className={cn("mt-0.5 truncate", ENTITY_FINDER_SUBTITLE_CLS)}>
              <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
            </p>
          ) : null}
        </div>
      </div>
    );
  },
  onSelect: (item, ctx) => {
    ctx.entityManager.openEntityManager({
      entityType: ENTITY_TYPES.TASK,
      entityId: (item.record as Task).id,
    });
  },
  createOnUnmatched: {
    label: "Create new task",
    fn: (query, ctx) => {
      const trimmed = query.trim();
      if (!trimmed) return;
      const newRecord = (
        ctx.project.extraTaskStore as { createRecord: (data?: object) => unknown }
      ).createRecord({ name: trimmed });
      openEntityEditor(ctx, EDITOR_KEYS.taskEditor, newRecord, { task: newRecord });
    },
    isEnabled: canCreate,
  },
};
